import Modal from "@/common/components/Modal";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { maintenanceSpecificationSchema, MaintenanceSpecificationValidationSchema } from "../../../schemas/update.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { EstablishmentLeadData } from "../../../types/establishmentLead.type";
import { useQueryClient } from "@tanstack/react-query";
import { FormTransformers } from "@/common/utils/formTransformers";
import MaintenanceSpecificationForm from "./MaintenanceSpecificationForm";
import { useUpdateEstablishmentLeadMaintenanceSpecification } from "@/features/establishmentLeadsFunnel/hooks/maintenanceModules/useUpdateEstablishmentLeadMaintenanceSpecification";
import { FileEditIcon } from "lucide-react";
import Button from "@/common/components/Button";

interface UpdateMaintenanceSpecificationModalProps {
  establishmentLead: EstablishmentLeadData;
  maintenanceSpecification: EstablishmentLeadData['maintenance_specifications'][number];
  alertText: {
    title: string;
    description: string;
  };
  disabled?: boolean;
}

const UpdateMaintenanceSpecificationModal = ({
  establishmentLead,
  maintenanceSpecification,
  alertText,
  disabled,
}: UpdateMaintenanceSpecificationModalProps) => {
  const queryClient = useQueryClient();

  const [open, setOpen] = useState(false);

  const formMethods = useForm<MaintenanceSpecificationValidationSchema>({
    resolver: zodResolver(maintenanceSpecificationSchema),
  });

  const {
    setError,
    handleSubmit,
    reset,
  } = formMethods;

  const {
    mutate: updateEstablishmentLeadMaintenanceSpecification,
    isPending: updateEstablishmentLeadMaintenanceSpecificationIsPending,
  } = useUpdateEstablishmentLeadMaintenanceSpecification({
    establishmentLeadId: establishmentLead.id,
    maintenanceSpecification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentLead', establishmentLead.id] });

      setOpen(false);
    },
    onError: FormTransformers.errorsTransformer<MaintenanceSpecificationValidationSchema>({
      setError,
    }),
  });

  const onSubmit: SubmitHandler<MaintenanceSpecificationValidationSchema> = (data) => updateEstablishmentLeadMaintenanceSpecification(data);

  useEffect(() => {
    if (open) {
      reset({
        equipment_category_specifications_attributes: maintenanceSpecification.equipment_category_specifications.map(
          ({ category }) => ({
            value: category.id,
            label: category.display_name,
          }),
        ),
        equipment_manufacturer_specifications_attributes: maintenanceSpecification.equipment_manufacturer_specifications.map(
          ({ manufacturer }) => ({
            value: manufacturer.id,
            label: manufacturer.display_name,
          }),
        ),
        system_specifications_attributes: maintenanceSpecification.system_specifications.map(
          ({ system }) => ({
            value: system.id,
            label: system.display_name,
          }),
        ),
      })
    }
  }, [open]);

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <Modal
        isOpen={open}
        setIsOpen={setOpen}
        triggerDisabled={disabled}
        Trigger={(
          <Button
            variant="link-white"
            className="w-full"
            operationKlassToCheckPermission="Admin::Funnel::Establishment::Leads::UpdateMaintenanceModels"
            disabled={disabled}
          >
            <FileEditIcon
              className="text-blue-800"
              strokeWidth={1}
            />
          </Button>
        )}
        title="Especificando atendimentos"
        useCancellableFooter
        confirmButtonClassName="bg-blue-700 rounded-br-lg"
        cancelButtonClassName="bg-white-200 rounded-bl-lg"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={updateEstablishmentLeadMaintenanceSpecificationIsPending}
        body={(
          <FormProvider {...formMethods}>
            <MaintenanceSpecificationForm
              alertText={alertText}
            />
          </FormProvider>
        )}
      />
    </div>
  );
}

export default UpdateMaintenanceSpecificationModal;
